import { styled } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import type { MouseEvent, ReactNode } from 'react';

type Props = {
  message: ReactNode;
  /** Relative date shown to the user, e.g. "il y a 2 heures". */
  date: string;
  /** Machine readable date of the `<time>` element (ISO 8601). */
  dateTime: string;
  unread?: boolean;
  /** Announced after the message of an unread row. */
  unreadLabel?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  'data-testid'?: string;
};

type OwnerState = {
  unread: boolean;
};

const DEFAULT_UNREAD_LABEL = 'Non lue';
const DOT_SIZE = 8;

const Row = styled('button')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `minmax(0, 1fr) ${DOT_SIZE}px`,
  columnGap: theme.spacing(1.5),
  rowGap: theme.spacing(0.25),
  alignItems: 'center',
  width: '100%',
  minHeight: theme.spacing(5.5),
  margin: 0,
  border: 0,
  borderRadius: theme.shape.borderRadius,
  paddingBlock: theme.spacing(1),
  paddingInline: theme.spacing(1),
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
  font: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shortest,
  }),
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
}));

const Message = styled('span')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  ...(ownerState.unread ? theme.typography.body2Emphasis : theme.typography.body2),
  gridColumn: 1,
  overflowWrap: 'anywhere',
}));

const DateText = styled('time')(({ theme }) => ({
  ...theme.typography.caption,
  gridColumn: 1,
  color: theme.palette.text.secondary,
  fontVariantNumeric: 'tabular-nums',
}));

const HiddenLabel = styled('span')(visuallyHidden);

const Dot = styled('span')(({ theme }) => ({
  gridColumn: 2,
  gridRow: '1 / span 2',
  width: DOT_SIZE,
  height: DOT_SIZE,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
}));

/**
 * One notification: its message, relative date and, while unread, a primary dot
 * (plus a screen reader label, so the state never relies on color alone).
 * The whole row is a single button opening the related item.
 */
export function NotificationListItem(props: Props) {
  const unread = Boolean(props.unread);
  const ownerState = { unread };

  return (
    <li data-testid={props['data-testid'] ?? 'notification-list-item'}>
      <Row type="button" onClick={props.onClick}>
        <Message ownerState={ownerState}>
          {props.message}
          {unread ? <HiddenLabel>{` (${props.unreadLabel ?? DEFAULT_UNREAD_LABEL})`}</HiddenLabel> : null}
        </Message>
        <DateText dateTime={props.dateTime}>{props.date}</DateText>
        {unread ? <Dot aria-hidden="true" /> : null}
      </Row>
    </li>
  );
}

export type { Props as NotificationListItemProps };
